/*
 * Lemonade Training Oy copy rights all right reserved.
 * The end date a member picks for a statutory cancellation is a day on the
 * gym's calendar, stored as 12:00 gym time so every client writes the same
 * instant for the same day. A gym without a zone passes the device's zone.
 * Nothing here throws; null means "no usable zone or date".
 */

import {
  addDaysToKey,
  calendarDateKey,
  wallClockToInstant,
  type CalendarDateKey,
} from "./zoneTime";

const STATUTORY_END_WALL_CLOCK = "12:00";

export type StatutoryEndRange = {
  firstKey: CalendarDateKey;
  lastKey: CalendarDateKey;
};

export function statutoryEndInstant(
  key: unknown,
  zone: unknown,
): number | null {
  const result = wallClockToInstant(key, STATUTORY_END_WALL_CLOCK, zone);
  return "instant" in result ? result.instant : null;
}

/** The gym's tomorrow: the server refuses an end date in the past. */
export function firstStatutoryEndKey(
  nowMs: number,
  zone: unknown,
): CalendarDateKey | null {
  if (!Number.isFinite(nowMs)) return null;
  return addDaysToKey(calendarDateKey(nowMs, zone), 1);
}

/**
 * The last day whose stored instant is not after the term's end. The server
 * refuses a later one, so a term ending before 12:00 gym time loses its last day.
 */
export function lastStatutoryEndKey(
  commitmentEndMs: number,
  zone: unknown,
): CalendarDateKey | null {
  if (!Number.isFinite(commitmentEndMs)) return null;
  const key = calendarDateKey(commitmentEndMs, zone);
  const noon = statutoryEndInstant(key, zone);
  if (noon === null) return null;
  return noon <= commitmentEndMs ? key : addDaysToKey(key, -1);
}

/**
 * The days a member may pick, or null when none can be chosen: unreadable
 * input, a term ending before the first pickable day, or no known term end
 * (the server refuses a dated end it cannot bound).
 */
export function statutoryEndRange(
  nowMs: number,
  commitmentEndMs: number | null,
  zone: unknown,
): StatutoryEndRange | null {
  if (commitmentEndMs === null) return null;
  const firstKey = firstStatutoryEndKey(nowMs, zone);
  if (firstKey === null) return null;
  const lastKey = lastStatutoryEndKey(commitmentEndMs, zone);
  if (lastKey === null || lastKey < firstKey) return null;
  return { firstKey, lastKey };
}
